import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Code,
  Wrap,
  WrapItem,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Icon,
  Tooltip,
  Button,
  useToast
} from '@chakra-ui/react';
import { InfoIcon, WarningIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const AdminAgents = ({ standalone = false }) => {
  const [agentsData, setAgentsData] = useState(null);
  const [aiCapabilitiesData, setAiCapabilitiesData] = useState(null);
  const [aiReasoningTools, setAiReasoningTools] = useState(null);
  const [featuresData, setFeaturesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  const Wrapper = React.Fragment;

  const toggleAgentStatus = async (agentId) => {
    try {
      const response = await fetchWithAuth(`/api/agents/toggle-status/${agentId}`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Agent Status Updated',
          description: result.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Refresh the data to show updated status
        fetchAgentsData();
      } else {
        throw new Error('Failed to toggle agent status');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to update agent status: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchAgentsData = async () => {
    try {
      setLoading(true);

      // Fetch agents data
      const agentsResponse = await fetchWithAuth('/api/agents/capabilities');
      const agentsData = await agentsResponse.json();
      setAgentsData(agentsData);

      // Fetch AI capabilities data
      const aiCapabilitiesResponse = await fetchWithAuth('/api/core-features-registry/ai-capabilities');
      const aiCapabilitiesData = await aiCapabilitiesResponse.json();
      setAiCapabilitiesData(aiCapabilitiesData);

      // Fetch AI reasoning tools
      const aiReasoningResponse = await fetchWithAuth('/api/core-features-registry/ai-reasoning-tools');
      const aiReasoningData = await aiReasoningResponse.json();
      setAiReasoningTools(aiReasoningData);

      // Fetch features data
      const featuresResponse = await fetchWithAuth('/api/core-features-registry/features');
      const featuresData = await featuresResponse.json();
      setFeaturesData(featuresData);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentsData();
  }, []);

  if (loading) return (
    <Wrapper>
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Spinner size="xl" />
      </Box>
    </Wrapper>
  );

  if (error) return (
    <Wrapper>
      <Alert status="error">
        <AlertIcon />
        Error loading agents data: {error}
      </Alert>
    </Wrapper>
  );

  const getAgentColor = (role) => {
    const colors = {
      strategic_planning: 'blue',
      research_insights: 'green',
      experience_design: 'purple',
      interface_design: 'orange',
      project_coordination: 'teal'
    };
    return colors[role] || 'gray';
  };

  const getCapabilityStatus = (capabilityId) => {
    // First check if it's an AI Reasoning Tool
    if (aiReasoningTools?.ai_reasoning_tools?.[capabilityId]) {
      return {
        enabled: true,
        type: 'ai_reasoning_tool',
        name: aiReasoningTools.ai_reasoning_tools[capabilityId].name,
        found_in_registry: true,
      };
    }

    // Then check if this capability exists in the Features Registry
    // Check both the ai_capabilities data and the features data
    for (const [featureId, feature] of Object.entries(aiCapabilitiesData?.ai_capabilities || {})) {
      if (feature.capabilities && feature.capabilities[capabilityId]) {
        return {
          enabled: feature.capabilities[capabilityId].enabled,
          feature: feature.feature_name,
          component: feature.component,
          type: 'component_capability',
          found_in_registry: true,
        };
      }
    }

    // Also check in the main features registry for general capabilities
    for (const [featureId, feature] of Object.entries(featuresData?.features || {})) {
      if (feature.capabilities && feature.capabilities.includes(capabilityId)) {
        return {
          enabled: true, // Assume enabled if in main registry
          feature: feature.name,
          component: feature.component,
          type: 'feature_capability',
          found_in_registry: true,
        };
      }
    }

    return { found_in_registry: false };
  };

  const getArchitecturalGaps = (agent) => {
    const gaps = [];
    if (!agent.core_products_access || agent.core_products_access.length === 0) gaps.push('core_products_access');
    if (!agent.primary_features || agent.primary_features.length === 0) gaps.push('primary_features');
    if (!agent.known_components || Object.keys(agent.known_components).length === 0) gaps.push('known_components');
    if (!agent.primary_ai_capabilities || agent.primary_ai_capabilities.length === 0) gaps.push('primary_ai_capabilities');
    return gaps;
  };

  return (
    <Wrapper>
      <Box width="100%" mx="auto" bg="white" p={6}>
        <VStack align="flex-start" spacing={6} width="100%">
          <Box>
            <Heading size="xl" mb={2}>AI Agents Architecture</Heading>
            <Text color="gray.600">
              Complete overview of AI agents, their capabilities, and architectural relationships
            </Text>
          </Box>

          <Tabs width="100%" colorScheme="blue">
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Agent Architecture</Tab>
              <Tab>AI Capabilities Matrix</Tab>
              <Tab>Architectural Gaps</Tab>
            </TabList>

            <TabPanels>
              {/* Overview Tab */}
              <TabPanel>
                <VStack spacing={6} align="start">
                  {/* Summary Cards */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} width="100%">
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">
                          {agentsData?.enabled_agents?.length || 0}/{Object.keys(agentsData?.agents || {}).length}
                        </Text>
                        <Text fontSize="sm" color="gray.600">Enabled/Total Agents</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">
                          {agentsData?.tool_categories?.ai_reasoning_tools?.length || 0}
                        </Text>
                        <Text fontSize="sm" color="gray.600">AI Tools</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">
                          {agentsData?.tool_categories?.system_features?.length || 0}
                        </Text>
                        <Text fontSize="sm" color="gray.600">System Features</Text>
                      </CardBody>
                    </Card>
                    <Card>
                      <CardBody>
                        <Text fontSize="2xl" fontWeight="bold">
                          {aiCapabilitiesData?.total_features_with_ai || 0}
                        </Text>
                        <Text fontSize="sm" color="gray.600">AI-Enabled Features</Text>
                      </CardBody>
                    </Card>
                  </SimpleGrid>

                  {/* Tool Categories Overview */}
                  <Box width="100%">
                    <Heading size="lg" mb={4}>Available Tool Categories</Heading>
                    <HStack spacing={6} align="start">
                      <Box flex="1">
                        <Text fontWeight="bold" mb={2}>AI Reasoning Tools</Text>
                        <Wrap>
                          {agentsData?.tool_categories?.ai_reasoning_tools?.map((tool) => (
                            <WrapItem key={tool}>
                              <Badge colorScheme="blue" variant="outline">{tool}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                      <Box flex="1">
                        <Text fontWeight="bold" mb={2}>System Features</Text>
                        <Wrap>
                          {agentsData?.tool_categories?.system_features?.map((feature) => (
                            <WrapItem key={feature}>
                              <Badge colorScheme="green" variant="outline">{feature}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    </HStack>
                  </Box>
                </VStack>
              </TabPanel>

              {/* Agent Architecture Tab */}
              <TabPanel>
                <Accordion allowMultiple>
                  {Object.entries(agentsData?.agents || {}).map(([stringKey, agent]) => {
                    const gaps = getArchitecturalGaps(agent);
                    return (
                      <AccordionItem key={stringKey}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <HStack>
                              <Heading size="md">{agent.name}</Heading>
                              <Badge colorScheme={getAgentColor(agent.role)}>{agent.role}</Badge>
                              <Badge colorScheme={agent.enabled !== false ? "green" : "red"}>
                                {agent.enabled !== false ? "Enabled" : "Disabled"}
                              </Badge>
                              <Badge variant="outline">{agent.missions.length} missions</Badge>
                              {gaps.length > 0 && (
                                <Tooltip label={`Missing: ${gaps.join(', ')}`}>
                                  <Icon as={WarningIcon} color="orange.500" />
                                </Tooltip>
                              )}
                            </HStack>
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <VStack align="start" spacing={6}>
                            {/* Agent Profile Overview */}
                            <Box width="100%" p={4} bg="gray.50" borderRadius="md">
                              <HStack justify="space-between" align="center" mb={3}>
                                <Text fontWeight="bold" fontSize="lg">Agent Profile</Text>
                                <Button
                                  size="sm"
                                  colorScheme={agent.enabled !== false ? "red" : "green"}
                                  variant="outline"
                                  onClick={() => toggleAgentStatus(agent.id)}
                                >
                                  {agent.enabled !== false ? "Disable Agent" : "Enable Agent"}
                                </Button>
                              </HStack>
                              <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
                                <Box>
                                  <Text fontWeight="semibold" mb={2}>Role:</Text>
                                  <Badge colorScheme={getAgentColor(agent.role)} size="lg">
                                    {agent.role}
                                  </Badge>
                                </Box>
                                <Box>
                                  <Text fontWeight="semibold" mb={2}>Status:</Text>
                                  <Badge colorScheme={agent.enabled !== false ? "green" : "red"} size="lg">
                                    {agent.enabled !== false ? "Enabled" : "Disabled"}
                                  </Badge>
                                </Box>
                                <Box>
                                  <Text fontWeight="semibold" mb={2}>Specialties:</Text>
                                  <Wrap>
                                    {agent.specialties?.map((specialty) => (
                                      <WrapItem key={specialty}>
                                        <Badge colorScheme={getAgentColor(agent.role)} variant="solid">
                                          {specialty}
                                        </Badge>
                                      </WrapItem>
                                    ))}
                                  </Wrap>
                                </Box>
                              </SimpleGrid>
                            </Box>

                            {/* Agent Personality & Prompt Information */}
                            <Tabs width="100%" variant="enclosed" size="sm">
                              <TabList>
                                <Tab>Personality</Tab>
                                <Tab>System Prompt</Tab>
                                <Tab>Architecture</Tab>
                              </TabList>
                              <TabPanels>
                                <TabPanel>
                                  <Box p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                                    <Text fontWeight="semibold" mb={2} color="blue.700">Agent Personality:</Text>
                                    <Text fontSize="sm" color="blue.600" lineHeight="1.6">
                                      {agent.personality || "No personality description available"}
                                    </Text>
                                  </Box>
                                </TabPanel>
                                <TabPanel>
                                  <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200" maxH="300px" overflowY="auto">
                                    <Text fontWeight="semibold" mb={2} color="green.700">System Prompt:</Text>
                                    <Code 
                                      p={2} 
                                      fontSize="xs" 
                                      whiteSpace="pre-wrap" 
                                      display="block" 
                                      bg="white"
                                      borderRadius="md"
                                    >
                                      {agent.systemPrompt || "No system prompt available"}
                                    </Code>
                                  </Box>
                                </TabPanel>
                                <TabPanel>
                                  {/* Architectural Information */}
                                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                                    <Box>
                                      <Text fontWeight="bold" mb={2}>
                                        AI Capabilities: 
                                        <Tooltip label="From primary_ai_capabilities array">
                                          <Icon as={InfoIcon} ml={1} boxSize={3} />
                                        </Tooltip>
                                      </Text>
                                      <Wrap>
                                        {agent.primary_ai_capabilities?.map((capability) => {
                                          const status = getCapabilityStatus(capability);
                                          return (
                                            <WrapItem key={capability}>
                                              <Badge 
                                                colorScheme={
                                                  !status.found_in_registry ? 'gray' :
                                                  status.type === 'ai_reasoning_tool' ? 'purple' :
                                                  status.enabled ? 'green' : 'red'
                                                }
                                                variant={status.found_in_registry ? 'solid' : 'outline'}
                                                title={
                                                  status.type === 'ai_reasoning_tool' ? `AI Tool: ${status.name}` :
                                                  status.type === 'component_capability' ? `Component: ${status.component}` :
                                                  'Not found in registry'
                                                }
                                              >
                                                {capability}
                                                {!status.found_in_registry && ' ⚠️'}
                                                {status.type === 'ai_reasoning_tool' && ' 🔧'}
                                              </Badge>
                                            </WrapItem>
                                          );
                                        }) || <Text fontSize="sm" color="gray.500">None configured</Text>}
                                      </Wrap>
                                    </Box>

                                    <Box>
                                      <Text fontWeight="bold" mb={2}>Core Products Access:</Text>
                                      <Wrap>
                                        {agent.core_products_access?.map((product) => (
                                          <WrapItem key={product}>
                                            <Badge colorScheme="purple" variant="outline" size="sm">{product}</Badge>
                                          </WrapItem>
                                        )) || <Text fontSize="sm" color="gray.500">Not configured</Text>}
                                      </Wrap>
                                    </Box>

                                    <Box>
                                      <Text fontWeight="bold" mb={2}>Primary Features:</Text>
                                      <Wrap>
                                        {agent.primary_features?.map((feature) => (
                                          <WrapItem key={feature}>
                                            <Badge colorScheme="cyan" variant="outline" size="sm">{feature}</Badge>
                                          </WrapItem>
                                        )) || <Text fontSize="sm" color="gray.500">Not configured</Text>}
                                      </Wrap>
                                    </Box>

                                    <Box>
                                      <Text fontSize="sm" fontWeight="bold" mb={2}>Known Components:</Text>
                                      <Wrap>
                                        {agent.known_components ? 
                                          Object.entries(agent.known_components).map(([component, featureId]) => (
                                            <WrapItem key={component}>
                                              <Badge colorScheme="teal" variant="outline" size="sm">
                                                {component} → {featureId}
                                              </Badge>
                                            </WrapItem>
                                          )) : <Text fontSize="sm" color="gray.500">Not configured</Text>}
                                      </Wrap>
                                    </Box>

                                    <Box>
                                      <Text fontSize="sm" fontWeight="bold" mb={2}>Architecture Context:</Text>
                                      <VStack align="start" spacing={1}>
                                        <HStack>
                                          <Badge colorScheme="purple" size="sm">
                                            {agent.getArchitectureContext ? "Dynamic" : "Static"}
                                          </Badge>
                                          <Text fontSize="xs" color="gray.600">
                                            {agent.getArchitectureContext ? "Has dynamic architecture context" : "Static configuration only"}
                                          </Text>
                                        </HStack>
                                        <HStack>
                                          <Badge colorScheme="blue" size="sm">
                                            {agent.getCapabilityContext ? "Dynamic" : "Static"}
                                          </Badge>
                                          <Text fontSize="xs" color="gray.600">
                                            {agent.getCapabilityContext ? "Has dynamic capability context" : "Static capabilities only"}
                                          </Text>
                                        </HStack>
                                      </VStack>
                                    </Box>
                                  </SimpleGrid>
                                </TabPanel>
                              </TabPanels>
                            </Tabs>

                            <Divider />

                            {/* Missions */}
                            {/* Agent ID Usage in Workflows */}
                            <Box width="100%" p={4} bg="blue.50" borderRadius="md">
                              <Text fontWeight="bold" mb={2}>Agent ID Usage:</Text>
                              <HStack spacing={4}>
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold">Numeric ID:</Text>
                                  <Badge colorScheme="blue" size="lg">{agent.id}</Badge>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold">String Key:</Text>
                                  <Code fontSize="sm">{stringKey}</Code>
                                </Box>
                                <Box>
                                  <Text fontSize="sm" fontWeight="semibold">Used in Workflows:</Text>
                                  <Badge colorScheme="green" variant="outline">Dynamic Resolution</Badge>
                                </Box>
                              </HStack>
                            </Box>

                            <Box width="100%">
                              <Text fontWeight="bold" mb={3}>Missions & Tools:</Text>
                              <Table size="sm" variant="striped">
                                <Thead>
                                  <Tr>
                                    <Th>Mission</Th>
                                    <Th>Description</Th>
                                    <Th>Tools</Th>
                                    <Th>Workflow</Th>
                                    <Th>Handoff To</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {Object.entries(agent.mission_details || {}).map(([missionId, mission]) => (
                                    <Tr key={missionId}>
                                      <Td>
                                        <VStack align="start" spacing={1}>
                                          <Text fontWeight="semibold">{mission.name}</Text>
                                          <Code fontSize="xs">{missionId}</Code>
                                        </VStack>
                                      </Td>
                                      <Td>
                                        <Text fontSize="sm">{mission.description}</Text>
                                      </Td>
                                      <Td>
                                        <VStack align="start" spacing={1}>
                                          {mission.tools?.map((tool) => (
                                            <Badge key={tool} size="sm" variant="outline">
                                              {tool}
                                            </Badge>
                                          ))}
                                        </VStack>
                                      </Td>
                                      <Td>
                                        <VStack align="start" spacing={1}>
                                          {mission.workflow?.map((step, idx) => (
                                            <Text key={idx} fontSize="xs">
                                              {idx + 1}. {step}
                                            </Text>
                                          ))}
                                        </VStack>
                                      </Td>
                                      <Td>
                                        {mission.handoff_to ? (
                                          <VStack align="start" spacing={1}>
                                            {mission.handoff_to.map((handoff) => (
                                              <Badge key={handoff} colorScheme="orange" size="sm">
                                                {handoff}
                                              </Badge>
                                            ))}
                                          </VStack>
                                        ) : (
                                          <Text fontSize="sm" color="gray.500">None</Text>
                                        )}
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </Box>
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </TabPanel>

              {/* AI Capabilities Matrix Tab */}
              <TabPanel>
                <VStack spacing={4} align="start">
                  <Text>
                    This shows the relationship between agents and AI capabilities from the Features Registry.
                  </Text>

                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Agent</Th>
                        <Th>AI Capabilities</Th>
                        <Th>Enabled/Total</Th>
                        <Th>Status</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.values(agentsData?.agents || {}).map((agent) => {
                        const capabilities = agent.primary_ai_capabilities || [];
                        const enabledCount = capabilities.filter(cap => {
                          const status = getCapabilityStatus(cap);
                          return status?.enabled;
                        }).length;

                        return (
                          <Tr key={agent.id}>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="semibold">{agent.name}</Text>
                                <Badge colorScheme={getAgentColor(agent.role)} size="sm">
                                  {agent.role}
                                </Badge>
                              </VStack>
                            </Td>
                            <Td>
                              <Wrap>
                                {capabilities.map((capability) => {
                                  const status = getCapabilityStatus(capability);

                                  let colorScheme = "gray";
                                  let tooltipText = "Unknown status";

                                  if (status?.found_in_registry) {
                                    if (status.type === 'ai_reasoning_tool') {
                                      colorScheme = "purple";
                                      tooltipText = `AI Reasoning Tool: ${status.name}`;
                                    } else {
                                      colorScheme = status.enabled ? "green" : "red";
                                      tooltipText = `${status.feature || 'Feature'} (${status.component || 'Component'}) - ${status.enabled ? 'Enabled' : 'Disabled'}`;
                                    }
                                  } else {
                                    colorScheme = "orange";
                                    tooltipText = "Not found in registry - may be legacy or custom capability";
                                  }

                                  return (
                                    <WrapItem key={capability}>
                                      <Tooltip label={tooltipText}>
                                        <Badge 
                                          colorScheme={colorScheme} 
                                          variant={status?.found_in_registry ? "solid" : "outline"}
                                          size="sm"
                                        >
                                          {capability}
                                          {!status?.found_in_registry && " ⚠️"}
                                          {status?.type === 'ai_reasoning_tool' && " 🔧"}
                                        </Badge>
                                      </Tooltip>
                                    </WrapItem>
                                  );
                                })}
                                {capabilities.length === 0 && (
                                  <Text fontSize="sm" color="gray.500">No AI capabilities configured</Text>
                                )}
                              </Wrap>
                            </Td>
                            <Td>
                              <Text>{enabledCount}/{capabilities.length}</Text>
                            </Td>
                            <Td>
                              {capabilities.length === 0 ? (
                                <Badge colorScheme="gray">Not Configured</Badge>
                              ) : enabledCount === capabilities.length ? (
                                <Badge colorScheme="green">Fully Enabled</Badge>
                              ) : enabledCount > 0 ? (
                                <Badge colorScheme="yellow">Partially Enabled</Badge>
                              ) : (
                                <Badge colorScheme="red">All Disabled</Badge>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </VStack>
              </TabPanel>

              {/* Architectural Gaps Tab */}
              <TabPanel>
                <VStack spacing={4} align="start">
                  <Alert status="info">
                    <AlertIcon />
                    This shows agents missing architectural configuration elements.
                  </Alert>

                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Agent</Th>
                        <Th>Missing Configuration</Th>
                        <Th>Impact</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.values(agentsData?.agents || {}).map((agent) => {
                        const gaps = getArchitecturalGaps(agent);
                        return (
                          <Tr key={agent.id}>
                            <Td>
                              <VStack align="start" spacing={1}>
                                <Text fontWeight="semibold">{agent.name}</Text>
                                <Badge colorScheme={getAgentColor(agent.role)} size="sm">
                                  {agent.role}
                                </Badge>
                              </VStack>
                            </Td>
                            <Td>
                              {gaps.length > 0 ? (
                                <Wrap>
                                  {gaps.map((gap) => (
                                    <WrapItem key={gap}>
                                      <Badge colorScheme="orange" variant="outline" size="sm">
                                        {gap}
                                      </Badge>
                                    </WrapItem>
                                  ))}
                                </Wrap>
                              ) : (
                                <Badge colorScheme="green" size="sm">Complete</Badge>
                              )}
                            </Td>
                            <Td>
                              {gaps.length > 0 ? (
                                <Text fontSize="sm" color="orange.600">
                                  Limited architectural integration
                                </Text>
                              ) : (
                                <Text fontSize="sm" color="green.600">
                                  Full architectural integration
                                </Text>
                              )}
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>

                  <Box p={4} bg="yellow.50" borderRadius="md" borderLeft="4px solid" borderColor="yellow.400">
                    <Text fontWeight="bold" mb={2}>Current Architecture Issues:</Text>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm">• AI capabilities are hardcoded in agent config instead of dynamic from Features Registry</Text>
                      <Text fontSize="sm">• Not all agents follow the same configuration template</Text>
                      <Text fontSize="sm">• Some agents lack core architectural elements (core_products_access, primary_features, known_components)</Text>
                      <Text fontSize="sm">• Design Lead has more complete configuration than other agents</Text>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>
    </Wrapper>
  );
};

export default AdminAgents;